/**
 * 画板工具栏状态 Store
 * 管理画板详情页的工具模式、面板状态等
 *
 * 注：UI 布局状态（图库折叠、工具栏可见性等）已迁移到 uiLayoutStore
 */

import { InteractionMode } from "board-core/plugin/InteractionModeManager";
import { defineStore } from "pinia";
import { ref, computed, watch, toRaw } from "vue";
import type { FissionReEditParams } from "@/views/workspace/board/detail/components/ImageContextToolbar/FissionPanel/types";
import type { DraftReEditParams } from "@/views/workspace/board/detail/components/ImageContextToolbar/DraftPanel/types";
import type { CanvasFreePerspectiveReEditParams } from "@/views/workspace/board/detail/components/ImageContextToolbar/CanvasFreePerspectivePanel/types";
import type { InpaintAttachPayload } from "@/views/workspace/board/detail/components/ImageContextToolbar/InpaintPanel/types";
import type { SplitLayer, SplitSource, SplitFlowEntry } from '@xmiles/holopix-components';
import type { SplitSessionRecord } from "@/views/workspace/board/detail/components/ImageContextToolbar/GameUiSplitterPanel/types";
import { SPLIT_TASK_ID_TAG } from "@/views/workspace/board/detail/components/ImageContextToolbar/GameUiSplitterPanel/constants";
import { registerScanLocks, unregisterScanLocks, pruneScanLocks, collectScanLockSet, type ScanLockWriteMode } from "./splitScanLock";

const DEFAULT_STATE = {
  activeTool: InteractionMode.POINTER,
  currentToolMode: "pointer"
};

export const useBoardToolbarStore = defineStore("infinite-canvas-toolbar", () => {
  // #region State - 交互模式
  /** 当前激活的交互工具模式 */
  const activeTool = ref<InteractionMode>(DEFAULT_STATE.activeTool);

  /** 当前工具的子模式（如画笔的具体类型） */
  const currentToolMode = ref(DEFAULT_STATE.currentToolMode);

  /** 是否处于临时拖拽状态（通过空格键或中键触发） */
  const isTemporaryDrag = ref(false);

  /**
   * 临时拖拽是否通过 DragModePlugin 平移。
   * 空格需要 true；中键自带 viewport 平移，必须 false，
   * 否则会与 lockTargetFind 争抢 skipTargetFind，松开后第一次点击选不中。
   */
  const temporaryDragPanViaPlugin = ref(false);

  /** 记录临时拖拽前的工具模式，用于结束拖拽后恢复 */
  const previousTool = ref<InteractionMode | null>(null);

  /** 是否处于快速编辑模式 (Tab键触发，显示聊天框) */
  const isQuickEditMode = ref(false);

  /** 是否正在拖拽对象 */
  const isObjectDragging = ref(false);

  /** 是否正在与视口交互 */
  const isViewportInteracting = ref(false);
  // #endregion

  // #region State - 面板状态
  /** 万能渲染面板是否可见 */
  const isRenderPanelVisible = ref(false);

  /** 万能渲染面板当前目标图层 CID */
  const renderTargetCid = ref("");

  /** 待回填的重新编辑参数（由 handleRenderPanelReEdit 设置，渲染面板消费后清空） */
  const pendingReEditParams = ref<Record<string, any> | null>(null);

  /** 视频生成面板是否可见 */
  const isVideoPanelVisible = ref(false);

  /** 视频生成面板当前目标图层 CID */
  const videoTargetCid = ref("");

  const pendingVideoReEditParams = ref<Record<string, any> | null>(null);

  const videoPanelPrefill = ref<null | { mode: "head-tail" | "image-to-video" | "start-end" | "reference" | "multimodal"; imageUrls: string[]; videoUrls: string[] }>(null);

  /** 相似图裂变面板是否可见 */
  const isFissionPanelVisible = ref(false);
  /** 相似图裂变面板当前目标图层 CID */
  const fissionTargetCid = ref("");
  /** 待回填的重新编辑参数 */
  const pendingFissionReEditParams = ref<FissionReEditParams | null>(null);

  /** 智能扩图面板是否可见 */
  const isOutpaintPanelVisible = ref(false);
  /** 智能扩图面板当前目标图层 CID */
  const outpaintTargetCid = ref("");
  /** 待回填的智能扩图重新编辑参数 */
  const pendingOutpaintReEditParams = ref<Record<string, any> | null>(null);

  /** 一键成稿面板是否可见 */
  const isDraftPanelVisible = ref(false);
  /** 一键成稿面板当前目标图层 CID */
  const draftTargetCid = ref("");
  /** 待回填的重新编辑参数 */
  const pendingDraftReEditParams = ref<DraftReEditParams | null>(null);
  // #endregion

  // #region State - 局部修改面板
  /** 局部修改面板是否可见 */
  const isInpaintPanelVisible = ref(false);
  /** 局部修改面板当前目标图层 CID */
  const inpaintTargetCid = ref("");
  /** 待回填的局部修改重新编辑参数 */
  const pendingInpaintReEditParams = ref<InpaintAttachPayload | null>(null);
  /** 局部修改面板：当前蒙版是否为空（无涂抹内容） */
  const isInpaintMaskEmpty = ref(true);
  /** 局部修改面板：涂抹历史是否可撤销（响应式暴露给 UI，FR-019） */
  const inpaintCanUndo = ref(false);
  /** 局部修改面板：涂抹历史是否可重做（响应式暴露给 UI，FR-019） */
  const inpaintCanRedo = ref(false);
  // #endregion

  // #region State - 重绘放大面板
  /** 重绘放大面板是否可见 */
  const isRepaintUpscalePanelVisible = ref(false);

  /** 重绘放大面板当前目标图层 CID */
  const repaintUpscaleTargetCid = ref("");

  /** 待回填的重绘放大重新编辑参数 */
  const pendingRepaintUpscaleReEditParams = ref<Record<string, any> | null>(null);
  // #endregion

  // #region State - 自由视角面板
  /** 自由视角面板是否可见 */
  const isFreePerspectivePanelVisible = ref(false);

  /** 自由视角面板当前目标图层 CID */
  const freePerspectiveTargetCid = ref("");

  /** 待回填的自由视角重新编辑参数 */
  const pendingFreePerspectiveReEditParams = ref<CanvasFreePerspectiveReEditParams | null>(null);

  /** 自由视角面板入口来源，用于埋点 free_canvas_source */
  const freePerspectiveSource = ref<'tool_panel' | 're_edit' | 'artwork_detail'>('tool_panel');
  // #endregion

  // #region State - Game UI Splitter 面板
  /** 全能拆分面板是否可见 */
  const isGameUiSplitterPanelVisible = ref(false);

  /** 全能拆分面板当前目标图层 CID */
  const gameUiSplitterTargetCid = ref('');

  /** 查看拆分详情请求（TopPanel 拆分图组工具条写入；GameUiSplitterPanel 挂载后消费） */
  const splitDetailRequestTaskId = ref('');
  /** 查看拆分详情请求：用户双击/操作的具体图组 fabric id（复制/重复落地会产生同 taskId 的多个图组，
   * 仅靠 taskId 无法区分；图组 id 复制时重新生成、保存后持久化，是唯一稳定锚点，用于继续拆分扫描/落地） */
  const splitDetailRequestGroupId = ref('');
  /** 外部传入的拆分图层（画廊「我的资产」module 87 记录写入；消费后清空；非空时优先于画布图组查找） */
  const splitDetailRequestLayers = ref<SplitLayer[] | null>(null);
  /** 画廊 module 87 拆分会话 clientId（即 ArtworkVersion.clientId，后端已确认；供详情修补） */
  const splitDetailRequestSplitClientId = ref('');
  /** 画廊 module 87 当前流程 revision（后端 versionList 返回 resultRevision；供详情修补 CAS） */
  const splitDetailRequestResultRevision = ref<number | null>(null);
  /** 画廊 module 87 所属作品 id（需求 73：修补/回滚必须用会话的 artworkId，而非当前画布） */
  const splitDetailRequestArtworkId = ref<number | null>(null);
  /** 资产浏览入口（我的资产/创作空间）标记仅查看：详情弹窗禁用排序/删除/修补等会话操作（消费后清空） */
  const splitDetailRequestViewOnly = ref(false);
  /** 画廊/资产来源的源拆分版本 id（message.result.versionId / GalleryItem.artworkVersionId）：
   * 复制到画布 copyResult 的 sourceVersionId，随详情请求消费写入注册表 */
  const splitDetailRequestSourceVersionId = ref<number | null>(null);

  /** 拆分会话注册表：拆分面板落地时写入、详情宿主读取/更新（替代薄壳内局部 Map，跨组件共享） */
  const splitSessionRegistry = ref<Record<string, SplitSessionRecord>>({});
  /** 详情弹窗当前图层：详情宿主读写；re-split 合并结果由拆分面板写回 */
  const splitDetailLayers = ref<SplitLayer[]>([]);
  /** 详情弹窗当前任务 id */
  const splitDetailTaskId = ref('');
  /** 详情来源标记：true=画廊 module 87 记录（无画布图组），false=画布图组 */
  const splitIsGalleryDetail = ref(false);
  /** 详情弹窗是否打开（顶层据此条件挂载详情宿主 DetailHost） */
  const isSplitDetailModalOpen = ref(false);
  /** re-split 请求：详情宿主写入（携带父图层 + 父拆图 source + 权威 bbox）、拆分面板消费后进入拆分流 */
  const splitReSplitRequest = ref<{
    layerUrl: string;
    parentLayer: SplitLayer | null;
    /** 用户操作的图组 fabric id（详情由双击图组打开时记录；复制/重复落地会产生同 taskId/clientId 的
     * 多个图组，继续拆分扫描/落地必须按此锚定到用户操作的那一个，而非数组首位） */
    groupId?: string;
    source?: SplitSource;
    /** 父图层权威 bbox（来自主任务 manifest，弹窗选区坐标系 = 父图层局部画布尺寸） */
    bbox?: [number, number, number, number];
  } | null>(null);

  /** 派生任务恢复请求（按会话 clientId 多槽，0913 排查 F-02/F-03）：创作记录卡「恢复」/编排器终态转交/
   * 快照恢复超时转后台三类写入方，GameUiSplitterPanel 消费后执行 RESUME 恢复。
   * 单槽时代并行两会话先后终态：后者写不进被静默丢弃且游标照推=结果永久丢失；消费长落地期间
   * 无条件置 null 也会抹掉新请求。多槽 upsert 后各会话互不覆盖，消费只摘除本会话槽位。
   * artworkId/boardId：编排器写入（会话真实作品/画板），消费时校验防跨画板——判据用 boardId
   * （画板主键不漂移；artworkId 随保存推进会误杀，0913）；
   * sourceImageUrl/canvasSize/flow：编排器终态转交时携带的快照字段——消费时快照可能已被
   * 终态清理，「首拆无图组」的 manifest 完整落地依赖这些字段自包含，不再回读快照 */
  const splitRecoveryRequests = ref<Record<string, {
    clientId: string;
    artworkId?: number;
    boardId?: number;
    sourceImageUrl?: string;
    canvasSize?: { width: number; height: number };
    flow?: 'split' | 'extract';
  }>>({});

  /** 写入/刷新某会话的恢复请求（幂等 upsert；整对象替换保证 watch 触发） */
  function putSplitRecoveryRequest(req: { clientId: string; artworkId?: number; boardId?: number; sourceImageUrl?: string; canvasSize?: { width: number; height: number }; flow?: 'split' | 'extract' }) {
    splitRecoveryRequests.value = { ...splitRecoveryRequests.value, [req.clientId]: req };
  }

  /** 摘除某会话的恢复请求槽（消费/跨画板丢弃用），返回摘除前的值（无则 null） */
  function takeSplitRecoveryRequest(clientId: string) {
    const req = splitRecoveryRequests.value[clientId] ?? null;
    if (req) {
      const next = { ...splitRecoveryRequests.value };
      delete next[clientId];
      splitRecoveryRequests.value = next;
    }
    return req;
  }

  /** 自动恢复进行中的会话 clientId（快照恢复/画布扫描恢复共用互斥集，防同一会话双恢复） */
  const resumingClientIds = ref<string[]>([]);

  /** 画廊全能拆分（module 87）记录待落地快照：按 taskId 索引（避免列表多条 module-87 记录 last-writer-wins 串用），
   *  ResultBlock 挂载后写入对应 taskId，列表项容器底部「定位/添加画布」点击时只取本记录任务。 */
  const splitGalleryPlaceSnapshots = ref<Record<string, {
    layers: SplitLayer[];
    taskId: string;
    clientId?: string;
    resultRevision?: number | null;
    artworkId?: number | null;
    /** 源拆分版本 id：复制到画布 copyResult 的 sourceVersionId（统一复制口径） */
    sourceVersionId?: number | null;
    /** 已落地副本图组 taskId（copyResult 落地后回写）：「定位」分支快路径 */
    placedTaskId?: string;
  }>>({});

  /** 待选拆分入口：工具栏 hover 下拉写入，GameUiSplitterPanel 挂载后消费并自动进入对应拆分流 */
  const gameUiSplitterPendingEntry = ref<SplitFlowEntry | null>(null);

  /** 高级输入框「开始拆分」请求：列表页写入（SPA 内跨路由传递），画板侧图落画布后消费并自动打开拆分面板 */
  const gameUiSplitterHeroRequest = ref<{ imageUrl: string } | null>(null);


  /** 全能拆分面板同步锁：拆分完成创建画布对象期间锁定，禁止选中变化触发面板关闭 */
  const gameUiSplitterSyncLock = ref(false);

  function setGameUiSplitterSyncLock(locked: boolean) {
    gameUiSplitterSyncLock.value = locked;
  }

  // 主拆分扫描覆盖层视觉态（需求 70：画布级常驻 DOM 渲染，与拆分面板生命周期解耦——
  // 面板因选中变化/画布平移/收起被关闭时，扫描动效与文案提示条不随面板卸载消失）。
  // 0912 并行复发修复：由全局单槽改为按会话 clientId 多实例（对齐派生绿光
  // splitDerivedScanOverlays 模式）——此前 B 发起 showScanOverlay 覆写单槽把 A 的
  // 任务视觉顶掉（「A 的拆分副本不见了」，A 终态落地才复现）。动画定时器与落点锚
  // 等非渲染态不入 store，统一在 scanOverlaySessions 模块按会话管理。
  const splitMainScanOverlays = ref<Record<string, {
    visible: boolean;
    /** 副本图 URL（继续拆分无副本图时为空串=纯绿光） */
    imgUrl: string;
    /** 轮播提示文案（空串=不渲染提示条） */
    tipText: string;
    /** 副本当前位置（滑出动画逐帧写入；画布坐标，画布级覆盖层据此 × viewportTransform 定位） */
    copyRect: { left: number; top: number; w: number; h: number } | null;
  }>>({});

  /** 单条派生任务扫描覆盖层状态（按 commandId 索引，支持并发多绿光；画布级常驻组件 SplitScanOverlay 渲染） */
  const splitDerivedScanOverlays = ref<Record<string, {
    visible: boolean;
    imgUrl: string;
    copyRect: { left: number; top: number; w: number; h: number } | null;
    tipText: string;
  }>>({});

  /** 修补任务后台运行中（需求 95）：提交修补后弹窗关闭回到画布，任务在 RepairModal 组件内继续，
   *  详情宿主据此保持挂载（避免卸载中断任务），并驱动画布绿光扫描 loading */
  const splitRepairRunning = ref(false);

  /** 被扫图层左上角状态角标的场景标记（0910 需求）：拆分流启动时写入，仅区分「拆分/继续拆分」
   *  文案（key 见 SPLIT_TASK_STATUS_LABELS）；角标显隐由 splitScanInProgress/splitRepairRunning
   *  派生、不读本字段，故无需流程收尾清理——下次流程启动必然覆写 */
  const splitStatusTaskKind = ref<'split' | 'continue'>('split');

  /**
   * 硬锁清单（按覆盖层 key 登记被扫画布对象，'main' 为主拆分/继续拆分共用主覆盖层，
   * 其余 key 为派生命令 commandId）。硬锁对象绿光期间 selectable/evented=false（不可选中/拖拽/
   * 双击进详情），覆盖层移除时由 SplitScanOverlay 恢复原值；其余画布对象不受影响、可正常编辑。
   * 纯图片源图走软锁（splitScanSoftLockObjects，只做入口耦合判定不改交互），分桶规则见 splitScanLock.ts。
   */
  const splitScanLockObjects = ref<Record<string, any[]>>({});
  /** 软锁清单（纯图片源图等非图组对象）：选中/拖动/详情放行，仅拆分入口置灰与删除守卫消费 */
  const splitScanSoftLockObjects = ref<Record<string, any[]>>({});
  const scanLockRefs = { hard: splitScanLockObjects, soft: splitScanSoftLockObjects };
  function setSplitScanLockObjects(key: string, objects: any[]) {
    splitScanLockObjects.value = { ...splitScanLockObjects.value, [key]: objects.filter(Boolean) };
  }
  /** 统一登记（按 SPLIT_GROUP_TAG 自动分硬/软桶，语义见 splitScanLock.ts）。'main' 用 append：
   *  并行主拆分共用 key，整键覆写会把在途任务的锁挤掉（0908 踩踏面）；派生/修补 commandId
   *  单命令独占用 replace。收尾配对 unregisterSplitScanLocks 按对象扣除 */
  const registerSplitScanLocks = (key: string, objects: any[], mode: ScanLockWriteMode = 'append') =>
    registerScanLocks(scanLockRefs, key, objects, mode);
  /** 按对象扣除：流程收尾只清自己登记的对象，不动并行在途任务的锁 */
  const unregisterSplitScanLocks = (key: string, objects: any[]) => unregisterScanLocks(scanLockRefs, key, objects);
  /** 死引用 GC（发起新扫描时调用）：清掉不在画布上的残留登记，在途活对象不受影响 */
  const pruneSplitScanLocks = (liveObjects: any[]) => pruneScanLocks(scanLockRefs, liveObjects);
  function clearSplitScanLockObjects(key: string) {
    if (!splitScanLockObjects.value[key] && !splitScanSoftLockObjects.value[key]) return;
    const hard = { ...splitScanLockObjects.value };
    const soft = { ...splitScanSoftLockObjects.value };
    delete hard[key];
    delete soft[key];
    splitScanLockObjects.value = hard;
    splitScanSoftLockObjects.value = soft;
  }
  /** 清除派生命令（非 'main' 覆盖层）的全部对象锁（面板卸载/整体复位时调用） */
  function clearAllDerivedScanLocks() {
    const keepMain = (record: Record<string, any[]>) => {
      const next: Record<string, any[]> = {};
      for (const [k, v] of Object.entries(record)) {
        if (k === 'main') next[k] = v;
      }
      return next;
    };
    splitScanLockObjects.value = keepMain(splitScanLockObjects.value);
    splitScanSoftLockObjects.value = keepMain(splitScanSoftLockObjects.value);
  }
  // #endregion

  // #region Getters - 计算属性
  /**
   * 拆分扫描绿光进行中（执行拆分/背景补全等待 + 派生恢复绿光）。
   * 语义分层（入口精准化）：
   * - 会话写命令（导出PSD/下载全部/修补/回滚/继续拆分）统一引用此守卫——命令通道是
   *   全局单例（单连接/clientId/命令序号，EXPORT 无 taskId 参数），与在途扫描冲突无法按对象拆分；
   * - 查看详情等只读入口改用 isScanCoupledObject 按图组精准判定，不再一刀切。
   * 被扫对象的交互由 splitScanLockObjects per-object 锁单独兜住。
   */
  /** 任一主拆分覆盖层可见（并行多会话）：写命令守卫/工具栏保活/快捷键拦截按「存在在途绿光」判定 */
  const hasVisibleSplitScanOverlay = computed(() =>
    Object.values(splitMainScanOverlays.value ?? {}).some((slot) => slot?.visible),
  );

  const splitScanInProgress = computed(() => {
    return (
      hasVisibleSplitScanOverlay.value ||
      Object.keys(splitDerivedScanOverlays.value ?? {}).length > 0
    );
  });

  /**
   * 当前全部被绿光锁定的对象集合（跨覆盖层 key 去重，SplitScanOverlay 上锁与工具条显隐共用）。
   * 登记的 fabric 对象存入 reactive ref 后会被深度代理，与 canvas.getObjects()/getActiveObject()
   * 返回的原始对象引用不一致——统一 toRaw 归一化，否则「选中对象是否被锁」与
   * 「getActiveObject === 锁对象」的引用比较永远失配（绿光上锁时的清选中即因此失效）。
   */
  const splitScanLockedObjectSet = computed(() => collectScanLockSet(splitScanLockObjects.value));

  /** 软锁对象集合（toRaw 归一化）：仅入口耦合判定（拆分置灰/删除守卫），不改 fabric 交互标记 */
  const splitScanSoftLockedObjectSet = computed(() => collectScanLockSet(splitScanSoftLockObjects.value));

  /** 选中对象是否正被绿光扫描锁定（修补/拆分扫描中不可操作，工具条应整体隐藏） */
  function isScanLockedObject(obj: any): boolean {
    if (!obj) return false;
    const set = splitScanLockedObjectSet.value;
    return set.has(obj) || set.has(toRaw(obj));
  }

  /**
   * 在途绿光扫描占用的拆分 taskId 集合（从锁对象提取）：被扫图组带 taskId，源图无 tag 不计入。
   * 同 taskId 的图组副本（复制/重复落地产生）与被扫对象读写同一会话数据，入口需一并拦截。
   */
  const splitScanBusyTaskIds = computed(() => {
    const set = new Set<string>();
    for (const obj of splitScanLockedObjectSet.value) {
      const taskId = obj?.[SPLIT_TASK_ID_TAG] as string | undefined;
      if (taskId) set.add(taskId);
    }
    return set;
  });

  /**
   * 拆分系入口精准守卫（绿光扫描期间）：仅与在途扫描会话耦合的对象被拦，
   * 其余图组的查看详情/工具条入口正常放行（详情打开为纯本地读，不碰会话命令）。
   * 耦合判定 = 硬锁对象 ∪ 软锁对象（发起拆分的纯图片源图，0909 起交互放开但入口仍拦）
   * ∪ 同 taskId 副本。会话写命令不适用此守卫——统一由 splitScanInProgress 全局拦截。
   */
  function isScanCoupledObject(obj: any): boolean {
    if (!obj || !splitScanInProgress.value) return false;
    if (isScanLockedObject(obj)) return true;
    const raw = toRaw(obj);
    if (splitScanSoftLockedObjectSet.value.has(raw)) return true;
    const taskId = obj?.[SPLIT_TASK_ID_TAG] as string | undefined;
    return !!taskId && splitScanBusyTaskIds.value.has(taskId);
  }

  /**
   * 是否应该隐藏上下文工具栏
   * 当拖拽画布(临时模式)、拖拽对象或视口交互时隐藏。
   * 绿光扫描不再隐藏工具栏：被扫对象已按对象锁定（不可选中故无工具条），
   * 绿光期间选中其他对象应正常出工具条、可编辑（拆分系入口仍由 splitScanInProgress 单独拦截）。
   */
  const shouldHideContextToolbar = computed(() => {
    return isTemporaryDrag.value || isObjectDragging.value || isViewportInteracting.value;
  });

  /**
   * 当前是否处于蒙版编辑模式
   * 包含：画笔、矩形选择、圆形选择、套索工具
   */
  const isMaskMode = computed(() => {
    return [InteractionMode.PAINT, InteractionMode.RECT_SELECT, InteractionMode.CIRCLE_SELECT, InteractionMode.LASSO].includes(activeTool.value);
  });

  /** 当前交互模式（activeTool 的别名） */
  const interactionMode = computed(() => activeTool.value);

  /** 是否有待消费的拆分详情请求（TopPanel/画廊写入、面板尚未消费）——用于挂载门控，避免无选中对象时请求滞留 */
  const hasPendingSplitDetailRequest = computed(() =>
    Boolean(splitDetailRequestTaskId.value) || (splitDetailRequestLayers.value?.length ?? 0) > 0
  );
  // #endregion

  // #region Actions - 交互模式操作
  /**
   * 设置当前激活的工具
   * @param tool 交互模式
   * @param toolMode (可选) 工具子模式
   */
  function setActiveTool(tool: InteractionMode, toolMode?: string) {
    activeTool.value = tool;
    if (toolMode) {
      currentToolMode.value = toolMode;
    }
  }

  /** 仅同步工具栏按钮高亮，不改变画布交互模式 */
  function setToolbarHighlight(toolMode: string) {
    currentToolMode.value = toolMode;
  }

  /**
   * 重置所有状态到默认值
   */
  function reset() {
    activeTool.value = DEFAULT_STATE.activeTool;
    currentToolMode.value = DEFAULT_STATE.currentToolMode;
  }

  /**
   * 开启临时拖拽模式（通常由空格键按下触发）
   * 会记录当前工具状态，以便后续恢复
   */
  function startTemporaryDrag(options?: { panViaPlugin?: boolean }) {
    if (isTemporaryDrag.value) return;
    if (isOutpaintPanelVisible.value) return;

    previousTool.value = activeTool.value;
    temporaryDragPanViaPlugin.value = options?.panViaPlugin ?? false;
    isTemporaryDrag.value = true;
  }

  /**
   * 结束临时拖拽模式（通常由空格键松开触发）
   * 恢复之前的工具状态
   */
  function endTemporaryDrag() {
    if (!isTemporaryDrag.value) return;

    isTemporaryDrag.value = false;
  }

  function clearTemporaryDragPanViaPlugin() {
    temporaryDragPanViaPlugin.value = false;
  }

  /**
   * 设置快速编辑模式
   * @param value 是否进入快速编辑模式
   */
  function setQuickEditMode(value: boolean) {
    isQuickEditMode.value = value;
  }

  /**
   * 切换快速编辑模式
   */
  function toggleQuickEditMode() {
    isQuickEditMode.value = !isQuickEditMode.value;
  }

  /**
   * 设置对象拖拽状态
   */
  function setObjectDragging(isDragging: boolean) {
    isObjectDragging.value = isDragging;
  }

  /**
   * 设置视口交互状态
   */
  function setViewportInteracting(isInteracting: boolean) {
    isViewportInteracting.value = isInteracting;
  }
  // #endregion

  // #region Actions - 面板操作
  function openRenderPanel(cid: string, reEditParams?: Record<string, any> | null) {
    isRenderPanelVisible.value = true;
    renderTargetCid.value = cid;
    pendingReEditParams.value = reEditParams || null;
  }

  function closeRenderPanel() {
    isRenderPanelVisible.value = false;
    renderTargetCid.value = "";
    pendingReEditParams.value = null;
  }

  function toggleRenderPanel(cid: string) {
    if (isRenderPanelVisible.value && renderTargetCid.value === cid) {
      closeRenderPanel();
    } else {
      openRenderPanel(cid);
    }
  }

  function openVideoPanel(cid: string) {
    isVideoPanelVisible.value = true;
    videoTargetCid.value = cid;
  }

  function openVideoPanelWithReEditParams(cid: string, reEditParams?: Record<string, any> | null) {
    isVideoPanelVisible.value = true;
    videoTargetCid.value = cid;
    pendingVideoReEditParams.value = reEditParams || null;
  }

  function closeVideoPanel() {
    isVideoPanelVisible.value = false;
    videoTargetCid.value = "";
    pendingVideoReEditParams.value = null;
    videoPanelPrefill.value = null;
  }

  function toggleVideoPanel(cid: string) {
    if (isVideoPanelVisible.value && videoTargetCid.value === cid) {
      closeVideoPanel();
    } else {
      openVideoPanel(cid);
    }
  }

  function setVideoPanelPrefill(payload: null | { mode: "head-tail" | "image-to-video" | "start-end" | "reference" | "multimodal"; imageUrls?: string[]; videoUrls?: string[] }) {
    if (!payload) {
      videoPanelPrefill.value = null;
      return;
    }
    videoPanelPrefill.value = {
      mode: payload.mode,
      imageUrls: [...(payload.imageUrls || [])],
      videoUrls: [...(payload.videoUrls || [])]
    };
  }

  function clearVideoPanelPrefill() {
    videoPanelPrefill.value = null;
  }
  // #endregion

  function openFissionPanel(cid: string, reEditParams?: FissionReEditParams | null) {
    isFissionPanelVisible.value = true;
    fissionTargetCid.value = cid;
    pendingFissionReEditParams.value = reEditParams || null;
  }

  function closeFissionPanel() {
    isFissionPanelVisible.value = false;
    fissionTargetCid.value = "";
    pendingFissionReEditParams.value = null;
  }

  function toggleFissionPanel(cid: string) {
    if (isFissionPanelVisible.value && fissionTargetCid.value === cid) {
      closeFissionPanel();
    } else {
      openFissionPanel(cid);
    }
  }

  function openOutpaintPanel(cid: string, reEditParams?: Record<string, any> | null) {
    isOutpaintPanelVisible.value = true;
    outpaintTargetCid.value = cid;
    pendingOutpaintReEditParams.value = reEditParams || null;
  }

  function closeOutpaintPanel() {
    isOutpaintPanelVisible.value = false;
    outpaintTargetCid.value = "";
    pendingOutpaintReEditParams.value = null;
  }

  function toggleOutpaintPanel(cid: string) {
    if (isOutpaintPanelVisible.value && outpaintTargetCid.value === cid) {
      closeOutpaintPanel();
    } else {
      openOutpaintPanel(cid);
    }
  }

  function openDraftPanel(cid: string, reEditParams?: DraftReEditParams | null) {
    isDraftPanelVisible.value = true;
    draftTargetCid.value = cid;
    pendingDraftReEditParams.value = reEditParams || null;
  }

  function closeDraftPanel() {
    isDraftPanelVisible.value = false;
    draftTargetCid.value = "";
    pendingDraftReEditParams.value = null;
  }

  function toggleDraftPanel(cid: string) {
    if (isDraftPanelVisible.value && draftTargetCid.value === cid) {
      closeDraftPanel();
    } else {
      openDraftPanel(cid);
    }
  }

  // #region Actions - 重绘放大面板操作
  function openRepaintUpscalePanel(cid: string, reEditParams?: Record<string, any> | null) {
    isRepaintUpscalePanelVisible.value = true;
    repaintUpscaleTargetCid.value = cid;
    pendingRepaintUpscaleReEditParams.value = reEditParams || null;
  }

  function closeRepaintUpscalePanel() {
    isRepaintUpscalePanelVisible.value = false;
    repaintUpscaleTargetCid.value = "";
    pendingRepaintUpscaleReEditParams.value = null;
  }

  function toggleRepaintUpscalePanel(cid: string) {
    if (isRepaintUpscalePanelVisible.value && repaintUpscaleTargetCid.value === cid) {
      closeRepaintUpscalePanel();
    } else {
      openRepaintUpscalePanel(cid);
    }
  }

  function openFreePerspectivePanel(cid: string, reEditParams?: CanvasFreePerspectiveReEditParams | null, source: 'tool_panel' | 're_edit' | 'artwork_detail' = 'tool_panel') {
    isFreePerspectivePanelVisible.value = true;
    freePerspectiveTargetCid.value = cid;
    pendingFreePerspectiveReEditParams.value = reEditParams || null;
    freePerspectiveSource.value = source;
  }

  function closeFreePerspectivePanel() {
    isFreePerspectivePanelVisible.value = false;
    freePerspectiveTargetCid.value = "";
    pendingFreePerspectiveReEditParams.value = null;
  }

  function toggleFreePerspectivePanel(cid: string) {
    if (isFreePerspectivePanelVisible.value && freePerspectiveTargetCid.value === cid) {
      closeFreePerspectivePanel();
    } else {
      openFreePerspectivePanel(cid);
    }
  }

  // #region Actions - 局部修改面板操作
  function openInpaintPanel(cid: string, reEditParams?: InpaintAttachPayload | null) {
    isInpaintPanelVisible.value = true;
    inpaintTargetCid.value = cid;
    pendingInpaintReEditParams.value = reEditParams || null;
  }

  function closeInpaintPanel() {
    isInpaintPanelVisible.value = false;
    inpaintTargetCid.value = "";
    pendingInpaintReEditParams.value = null;
    isInpaintMaskEmpty.value = true;
    // 退出面板时重置涂抹历史可用性标志（FR-019）
    inpaintCanUndo.value = false;
    inpaintCanRedo.value = false;
  }

  /**
   * 设置局部修改面板蒙版是否为空
   * @param empty true 表示当前没有涂抹内容，false 表示已有涂抹
   */
  function setInpaintMaskEmpty(empty: boolean) {
    isInpaintMaskEmpty.value = empty;
  }

  /**
   * 设置局部修改涂抹历史的可用性标志（FR-019）
   * 由 useInpaintMaskHistory composable 在历史栈状态变化时调用，
   * ZoomController 的撤销/重做按钮 computed 追踪这两个字段实现响应式禁用态。
   * @param canUndo 是否可撤销
   * @param canRedo 是否可重做
   */
  function setInpaintHistoryFlags(canUndo: boolean, canRedo: boolean) {
    inpaintCanUndo.value = canUndo;
    inpaintCanRedo.value = canRedo;
  }

  function toggleInpaintPanel(cid: string) {
    if (isInpaintPanelVisible.value && inpaintTargetCid.value === cid) {
      closeInpaintPanel();
    } else {
      openInpaintPanel(cid);
    }
  }
  // #endregion

  // #region Actions - Game UI Splitter 面板操作
  function openGameUiSplitterPanel(cid: string) {
    isGameUiSplitterPanelVisible.value = true;
    gameUiSplitterTargetCid.value = cid;
  }

  function closeGameUiSplitterPanel() {
    isGameUiSplitterPanelVisible.value = false;
    gameUiSplitterTargetCid.value = '';
  }

  function toggleGameUiSplitterPanel(cid: string) {
    if (isGameUiSplitterPanelVisible.value && gameUiSplitterTargetCid.value === cid) {
      closeGameUiSplitterPanel();
    } else {
      openGameUiSplitterPanel(cid);
    }
  }
  // #endregion

  // #region Watchers - 互斥逻辑
  // 渲染面板与快速编辑互斥
  watch(isRenderPanelVisible, (visible) => {
    if (visible && isQuickEditMode.value) {
      isQuickEditMode.value = false;
    }
  });
  watch(isQuickEditMode, (editing) => {
    if (editing && isRenderPanelVisible.value) {
      closeRenderPanel();
    }
  });

  // 视频面板与渲染面板互斥
  watch(isVideoPanelVisible, (visible) => {
    if (visible && isRenderPanelVisible.value) {
      closeRenderPanel();
    }
  });
  watch(isRenderPanelVisible, (visible) => {
    if (visible && isVideoPanelVisible.value) {
      closeVideoPanel();
    }
    if (visible && isRepaintUpscalePanelVisible.value) {
      closeRepaintUpscalePanel();
    }
  });

  // 重绘放大面板与其他面板互斥
  watch(isRepaintUpscalePanelVisible, (visible) => {
    if (visible) {
      if (isRenderPanelVisible.value) closeRenderPanel();
      if (isVideoPanelVisible.value) closeVideoPanel();
      if (isFissionPanelVisible.value) closeFissionPanel();
      if (isOutpaintPanelVisible.value) closeOutpaintPanel();
      if (isDraftPanelVisible.value) closeDraftPanel();
      if (isFreePerspectivePanelVisible.value) closeFreePerspectivePanel();
      if (isInpaintPanelVisible.value) closeInpaintPanel();
    }
  });
  watch(isRenderPanelVisible, (visible) => {
    if (visible && isRepaintUpscalePanelVisible.value) {
      closeRepaintUpscalePanel();
    }
    if (visible && isFreePerspectivePanelVisible.value) {
      closeFreePerspectivePanel();
    }
    if (visible && isInpaintPanelVisible.value) {
      closeInpaintPanel();
    }
  });
  watch(isVideoPanelVisible, (visible) => {
    if (visible && isRepaintUpscalePanelVisible.value) {
      closeRepaintUpscalePanel();
    }
    if (visible && isFreePerspectivePanelVisible.value) {
      closeFreePerspectivePanel();
    }
    if (visible && isInpaintPanelVisible.value) {
      closeInpaintPanel();
    }
  });

  // 自由视角面板与其他面板互斥
  watch(isFreePerspectivePanelVisible, (visible) => {
    if (visible) {
      if (isRenderPanelVisible.value) closeRenderPanel();
      if (isVideoPanelVisible.value) closeVideoPanel();
      if (isFissionPanelVisible.value) closeFissionPanel();
      if (isOutpaintPanelVisible.value) closeOutpaintPanel();
      if (isDraftPanelVisible.value) closeDraftPanel();
      if (isRepaintUpscalePanelVisible.value) closeRepaintUpscalePanel();
      if (isInpaintPanelVisible.value) closeInpaintPanel();
    }
  });
  // #endregion

  // 裂变面板与渲染面板、视频面板互斥
  watch(isFissionPanelVisible, (visible) => {
    if (visible) {
      if (isRenderPanelVisible.value) closeRenderPanel();
      if (isVideoPanelVisible.value) closeVideoPanel();
      if (isRepaintUpscalePanelVisible.value) closeRepaintUpscalePanel();
      if (isFreePerspectivePanelVisible.value) closeFreePerspectivePanel();
    }
  });
  watch(isRenderPanelVisible, (visible) => {
    if (visible && isFissionPanelVisible.value) {
      closeFissionPanel();
    }
  });
  watch(isVideoPanelVisible, (visible) => {
    if (visible && isFissionPanelVisible.value) {
      closeFissionPanel();
    }
  });

  // 智能扩图面板与其他面板互斥
  watch(isOutpaintPanelVisible, (visible) => {
    if (visible) {
      if (isRenderPanelVisible.value) closeRenderPanel();
      if (isVideoPanelVisible.value) closeVideoPanel();
      if (isRepaintUpscalePanelVisible.value) closeRepaintUpscalePanel();
      if (isFreePerspectivePanelVisible.value) closeFreePerspectivePanel();
    }
  });
  watch(isRenderPanelVisible, (visible) => {
    if (visible && isOutpaintPanelVisible.value) {
      closeOutpaintPanel();
    }
  });
  watch(isVideoPanelVisible, (visible) => {
    if (visible && isOutpaintPanelVisible.value) {
      closeOutpaintPanel();
    }
  });
  watch(isQuickEditMode, (editing) => {
    if (editing && isOutpaintPanelVisible.value) {
      closeOutpaintPanel();
    }
  });

  // 一键成稿面板与渲染面板、视频面板、裂变面板互斥
  watch(isDraftPanelVisible, (visible) => {
    if (visible) {
      if (isRenderPanelVisible.value) closeRenderPanel();
      if (isVideoPanelVisible.value) closeVideoPanel();
      if (isFissionPanelVisible.value) closeFissionPanel();
      if (isRepaintUpscalePanelVisible.value) closeRepaintUpscalePanel();
      if (isFreePerspectivePanelVisible.value) closeFreePerspectivePanel();
      if (isInpaintPanelVisible.value) closeInpaintPanel();
    }
  });
  watch(isRenderPanelVisible, (visible) => {
    if (visible && isDraftPanelVisible.value) {
      closeDraftPanel();
    }
  });
  watch(isVideoPanelVisible, (visible) => {
    if (visible && isDraftPanelVisible.value) {
      closeDraftPanel();
    }
  });
  watch(isFissionPanelVisible, (visible) => {
    if (visible && isDraftPanelVisible.value) {
      closeDraftPanel();
    }
  });

  // 局部修改面板与其他面板互斥
  watch(isInpaintPanelVisible, (visible) => {
    if (visible) {
      if (isRenderPanelVisible.value) closeRenderPanel();
      if (isVideoPanelVisible.value) closeVideoPanel();
      if (isFissionPanelVisible.value) closeFissionPanel();
      if (isOutpaintPanelVisible.value) closeOutpaintPanel();
      if (isDraftPanelVisible.value) closeDraftPanel();
      if (isRepaintUpscalePanelVisible.value) closeRepaintUpscalePanel();
      if (isFreePerspectivePanelVisible.value) closeFreePerspectivePanel();
    }
  });
  watch(isRenderPanelVisible, (visible) => {
    if (visible && isInpaintPanelVisible.value) closeInpaintPanel();
  });
  watch(isVideoPanelVisible, (visible) => {
    if (visible && isInpaintPanelVisible.value) closeInpaintPanel();
  });
  watch(isFissionPanelVisible, (visible) => {
    if (visible && isInpaintPanelVisible.value) closeInpaintPanel();
  });
  watch(isOutpaintPanelVisible, (visible) => {
    if (visible && isInpaintPanelVisible.value) closeInpaintPanel();
  });
  watch(isRepaintUpscalePanelVisible, (visible) => {
    if (visible && isInpaintPanelVisible.value) closeInpaintPanel();
  });

  // 全能拆分面板与其他面板互斥
  watch(isGameUiSplitterPanelVisible, (visible) => {
    if (visible) {
      if (isRenderPanelVisible.value) closeRenderPanel();
      if (isVideoPanelVisible.value) closeVideoPanel();
      if (isFissionPanelVisible.value) closeFissionPanel();
      if (isOutpaintPanelVisible.value) closeOutpaintPanel();
      if (isDraftPanelVisible.value) closeDraftPanel();
      if (isInpaintPanelVisible.value) closeInpaintPanel();
      if (isRepaintUpscalePanelVisible.value) closeRepaintUpscalePanel();
      if (isFreePerspectivePanelVisible.value) closeFreePerspectivePanel();
    }
  });
  watch(isRenderPanelVisible, (v) => { if (v && isGameUiSplitterPanelVisible.value) closeGameUiSplitterPanel(); });
  watch(isVideoPanelVisible, (v) => { if (v && isGameUiSplitterPanelVisible.value) closeGameUiSplitterPanel(); });
  watch(isFissionPanelVisible, (v) => { if (v && isGameUiSplitterPanelVisible.value) closeGameUiSplitterPanel(); });
  watch(isOutpaintPanelVisible, (v) => { if (v && isGameUiSplitterPanelVisible.value) closeGameUiSplitterPanel(); });
  watch(isDraftPanelVisible, (v) => { if (v && isGameUiSplitterPanelVisible.value) closeGameUiSplitterPanel(); });
  watch(isInpaintPanelVisible, (v) => { if (v && isGameUiSplitterPanelVisible.value) closeGameUiSplitterPanel(); });
  watch(isRepaintUpscalePanelVisible, (v) => { if (v && isGameUiSplitterPanelVisible.value) closeGameUiSplitterPanel(); });
  watch(isFreePerspectivePanelVisible, (v) => { if (v && isGameUiSplitterPanelVisible.value) closeGameUiSplitterPanel(); });

  return {
    // State - 交互模式
    activeTool,
    currentToolMode,
    isTemporaryDrag,
    temporaryDragPanViaPlugin,
    previousTool,
    isQuickEditMode,
    isObjectDragging,
    isViewportInteracting,

    // State - 面板状态
    isRenderPanelVisible,
    renderTargetCid,
    pendingReEditParams,
    isVideoPanelVisible,
    videoTargetCid,
    pendingVideoReEditParams,
    videoPanelPrefill,
    isFissionPanelVisible,
    fissionTargetCid,
    pendingFissionReEditParams,

    // State - 智能扩图
    isOutpaintPanelVisible,
    outpaintTargetCid,
    pendingOutpaintReEditParams,

    isDraftPanelVisible,
    draftTargetCid,
    pendingDraftReEditParams,

    // State - 重绘放大面板
    isRepaintUpscalePanelVisible,
    repaintUpscaleTargetCid,
    pendingRepaintUpscaleReEditParams,

    // State - 自由视角面板
    isFreePerspectivePanelVisible,
    freePerspectiveTargetCid,
    pendingFreePerspectiveReEditParams,

    // State - 局部修改面板
    isInpaintPanelVisible,
    inpaintTargetCid,
    pendingInpaintReEditParams,
    isInpaintMaskEmpty,
    inpaintCanUndo,
    inpaintCanRedo,

    freePerspectiveSource,

    // State - Game UI Splitter 面板
    isGameUiSplitterPanelVisible,
    gameUiSplitterTargetCid,
    splitDetailRequestTaskId,
    splitDetailRequestGroupId,
    splitDetailRequestLayers,
    splitDetailRequestSplitClientId,
    splitDetailRequestResultRevision,
    splitDetailRequestArtworkId,
    splitDetailRequestViewOnly,
    splitDetailRequestSourceVersionId,
    splitSessionRegistry,
    splitDetailLayers,
    splitDetailTaskId,
    splitIsGalleryDetail,
    isSplitDetailModalOpen,
    splitReSplitRequest,
    splitRecoveryRequests,
    putSplitRecoveryRequest,
    takeSplitRecoveryRequest,
    resumingClientIds,
    splitGalleryPlaceSnapshots,

    // Getters
    isMaskMode,
    interactionMode,
    shouldHideContextToolbar,
    hasPendingSplitDetailRequest,

    // Actions - 交互模式
    setActiveTool,
    setToolbarHighlight,
    reset,
    startTemporaryDrag,
    endTemporaryDrag,
    clearTemporaryDragPanViaPlugin,
    setQuickEditMode,
    toggleQuickEditMode,
    setObjectDragging,
    setViewportInteracting,

    // Actions - 面板操作
    openRenderPanel,
    closeRenderPanel,
    toggleRenderPanel,
    openVideoPanel,
    openVideoPanelWithReEditParams,
    closeVideoPanel,
    setVideoPanelPrefill,
    clearVideoPanelPrefill,
    toggleVideoPanel,
    openFissionPanel,
    closeFissionPanel,
    toggleFissionPanel,
    openOutpaintPanel,
    closeOutpaintPanel,
    toggleOutpaintPanel,
    openDraftPanel,
    closeDraftPanel,
    toggleDraftPanel,

    // Actions - 重绘放大面板操作
    openRepaintUpscalePanel,
    closeRepaintUpscalePanel,
    toggleRepaintUpscalePanel,
    openFreePerspectivePanel,
    closeFreePerspectivePanel,
    toggleFreePerspectivePanel,

    // Actions - 局部修改面板操作
    openInpaintPanel,
    closeInpaintPanel,
    toggleInpaintPanel,
    setInpaintMaskEmpty,
    setInpaintHistoryFlags,

    // Actions - Game UI Splitter 面板操作
    openGameUiSplitterPanel,
    closeGameUiSplitterPanel,
    toggleGameUiSplitterPanel,

    // State - Game UI Splitter 同步锁
    gameUiSplitterSyncLock,
    setGameUiSplitterSyncLock,

    // State - Game UI Splitter 扫描覆盖层（画布级常驻 DOM 渲染；主覆盖层按会话多实例）
    splitMainScanOverlays,
    hasVisibleSplitScanOverlay,
    splitDerivedScanOverlays,
    splitScanLockObjects,
    splitScanSoftLockObjects,
    setSplitScanLockObjects,
    registerSplitScanLocks,
    unregisterSplitScanLocks,
    pruneSplitScanLocks,
    clearSplitScanLockObjects,
    clearAllDerivedScanLocks,

    // State - Game UI Splitter 修补任务后台运行中（需求 95）
    splitRepairRunning,

    // State - 画布左上角任务状态标签场景标记（0910 需求，key 见 SPLIT_TASK_STATUS_LABELS）
    splitStatusTaskKind,

    // Getter - 拆分扫描绿光进行中（会话写命令统一守卫；只读入口用 isScanCoupledObject 精准判定）
    splitScanInProgress,
    splitScanLockedObjectSet,
    splitScanSoftLockedObjectSet,
    isScanLockedObject,
    splitScanBusyTaskIds,
    isScanCoupledObject,

    // State - Game UI Splitter 待选拆分入口（工具栏 hover 下拉写入）
    gameUiSplitterPendingEntry,
    gameUiSplitterHeroRequest,
  };
});
